import React, { useEffect, useState } from 'react';
import * as R from 'ramda';
import { Datagrid, Error, Loading, useQuery } from 'react-admin';
import { Box } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import moment from 'moment-timezone';
import LastProgressDateChip from 'resources/common/LastProgressDateChip';
import { FullNameField } from 'resources/students/fields';
import { OfflineList } from 'resources/common';
import { getLastCourseUpdate } from 'utils';
import { authServer } from 'auth/keycloak';

const LastProgress = ({ course, record = {} }) => {
  return (
    <Typography variant="body2" align="left">
      {record[course].last_progress.name}
    </Typography>
  );
};

const TotalHoursConnected = ({ course, record = {} }) => {
  const hours = R.path(
    [course, 'student_metrics', 'total_hours_connected'],
    record,
  );

  return (
    <Typography variant="body2" align="left">
      {hours != null ? hours.toFixed(1) : 'N/A'}
    </Typography>
  );
};

const FirstConnection = ({ course, record = {} }) => {
  const firstConnectionDate = R.path(
    [course, 'first_connection', 'date'],
    record,
  );

  if (R.isNil(firstConnectionDate)) {
    return (
      <Typography variant="body2" align="left">
        N/A
      </Typography>
    );
  }

  const formattedDate = moment(firstConnectionDate).format('Do MMMM YYYY');

  return (
    <Typography variant="body2" align="left">
      {formattedDate}
    </Typography>
  );
};

const LastConnection = ({ course, record = {} }) => {
  const lastConnectionDate = R.path(
    [course, 'last_connection', 'date'],
    record,
  );

  if (R.isNil(lastConnectionDate)) {
    return (
      <Typography variant="body2" align="left">
        N/A
      </Typography>
    );
  }

  const formattedDate = moment(lastConnectionDate).format('Do MMMM YYYY');

  return (
    <Typography variant="body2" align="left">
      {formattedDate}
    </Typography>
  );
};

const fetchCohortStudentMetrics = async (cohortId) => {
  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_HOST}/v1/cohorts/${cohortId}/student-metrics`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${authServer.token}`,
      },
    },
  );

  if (response.status === 503) {
    throw new Error('Student metrics temporarily unavailable');
  }

  if (response.status === 500) {
    throw new Error('Failed to retrieve student metrics');
  }

  if (!response.ok) {
    throw new Error('Failed to fetch student metrics');
  }

  return response.json();
};

const ByLastProgressSubtab = (props) => {
  const { record, course } = props;
  const [students, setStudents] = useState([]);

  const {
    data: apiStudents,
    loading: studentsLoading,
    error: studentsError,
  } = useQuery({
    type: 'getManyReference',
    resource: 'students',
    payload: {
      target: 'cohort',
      id: record.id,
      pagination: {
        perPage: 1000,
      },
      filter: { course: true },
      sort: { field: 'first_name', order: 'ASC' },
    },
  });

  useEffect(() => {
    if (apiStudents) {
      const tableData = R.pipe(
        R.map((student) => {
          const lastProgressDate = getLastCourseUpdate(course, student);
          return R.mergeDeepRight(student, {
            [course]: {
              last_progress: lastProgressDate,
            },
          });
        }),
      )(apiStudents);
      setStudents(tableData);
    }
  }, [apiStudents, course]);

  const cohortId = record?.id;

  // Fetch y fusión de datos de métricas
  useEffect(() => {
    const fetchMetrics = async () => {
      if (!authServer.isRoles(['admin', 'fundae'])) {
        return;
      }
      if (!cohortId || !apiStudents) {
        return;
      }

      try {
        const response = await fetchCohortStudentMetrics(cohortId);
        // La API devuelve { meta: {}, result: [...] }
        const metrics = response.result || [];

        // Transform metrics array into an object keyed by student_id
        // student_id ya es un string (MongoDB ID), no necesitamos parseInt
        const metricsByStudentId = R.indexBy(R.prop('student_id'), metrics);

        // Merge metrics into existing students data
        setStudents((prevStudents) =>
          R.map((student) => {
            const studentMetricsData = metricsByStudentId[student.id];
            if (!studentMetricsData) {
              return student;
            }

            return R.mergeDeepRight(student, {
              [course]: {
                student_metrics: {
                  total_hours_connected: studentMetricsData.total_hours_connected,
                  total_sessions: studentMetricsData.total_sessions,
                },
                first_connection: {
                  date: studentMetricsData.first_connection,
                },
                last_connection: {
                  date: studentMetricsData.last_connection,
                },
              },
            });
          }, prevStudents)
        );
      } catch (error) {
        console.error('Error fetching metrics:', error);
      }
    };

    fetchMetrics();
  }, [cohortId, course, apiStudents]);

  if (studentsLoading)
    return (
      <Box p="16px">
        <Loading />
      </Box>
    );
  if (studentsError)
    return (
      <Box p="16px">
        <Error error={studentsError} errorInfo={studentsError} />
      </Box>
    );

  if (R.isEmpty(students)) {
    return null;
  }

  return (
    <OfflineList
      list={students}
      uniqueKey="id"
      defaultSort={{ field: 'first_name', order: 'ASC' }}
      sorters={{
        last_progress: R.pipe(R.pathOr([], [course, 'last_progress', 'date'])),
      }}
    >
      <Datagrid
        rowClick={(id) => {
          window.open(`/#/students/${id}/show/${course}_progress`, '_blank');
        }}
      >
        <FullNameField />
        <LastProgressDateChip
          label="Last progress date"
          source="last_progress"
          course={course}
        />
        <LastProgress course={course} label="Last progress" />
        {authServer.isRoles(['admin', 'fundae']) && (
          <TotalHoursConnected course={course} label="Total hours connected" />
        )}
        {authServer.isRoles(['admin', 'fundae']) && (
          <FirstConnection course={course} label="First connection" />
        )}
        {authServer.isRoles(['admin', 'fundae']) && (
          <LastConnection course={course} label="Last connection" />
        )}
      </Datagrid>
    </OfflineList>
  );
};

export default ByLastProgressSubtab;
